import React from "react";
import { Link } from "react-router-dom";

const today = "February 2026";

const SECTIONS = {
  terms: {
    title: "Terms of Service",
    eyebrow: "Legal",
    body: [
      ["Welcome to Bud Link",
        "Bud Link (\"we\", \"us\") operates a search engine that indexes legally-licensed cannabis dispensaries operating under Pennsylvania's Medical Marijuana Act (35 P.S. § 10231.101 et seq.). By using this site you accept these Terms."],
      ["Eligibility",
        "You must be 21 years of age or older to use Bud Link. Pennsylvania medical cannabis purchases require a state-issued patient ID — we do not sell, ship, deliver, or transfer any cannabis product. Bud Link is purely informational."],
      ["What Bud Link is not",
        "Bud Link is NOT a dispensary, retailer, courier, or pharmacy. We do not warrant pricing or stock; the dispensary listed against any product is the source of truth. Always verify with the dispensary before traveling."],
      ["Acceptable use",
        "You may not scrape, resell, or republish Bud Link's data, attempt to disrupt our infrastructure, impersonate a dispensary you do not own, or use the service to facilitate any transaction that would be unlawful in Pennsylvania."],
      ["Dispensary partner terms",
        "Dispensary accounts: by uploading menu data (via CSV, JSON feed, or partner API) you grant Bud Link a non-exclusive license to display that data in our consumer search experience. You retain all rights. You're responsible for the accuracy and legality of every item you submit."],
      ["Paid placements",
        "Boosted product listings and featured dispensary placements are paid advertising. They are visually marked as Sponsored / Featured per the FTC's Endorsement Guides."],
      ["Disclaimers",
        "The service is provided \"as is\". We disclaim warranties of merchantability, fitness for a particular purpose, and non-infringement to the maximum extent permitted by law."],
      ["Liability",
        "To the maximum extent permitted by law, Bud Link's aggregate liability is limited to the amount you paid us in the prior 12 months (zero, for consumer users)."],
      ["Governing law",
        "These Terms are governed by the laws of the Commonwealth of Pennsylvania. Any dispute will be resolved in the state or federal courts located in Berks County, PA."],
      ["Contact",
        "Questions about these Terms: BudLink@LinkLitePro.com"],
    ],
  },
  privacy: {
    title: "Privacy Policy",
    eyebrow: "Legal",
    body: [
      ["What we collect",
        "Anonymous visitors: aggregate analytics, IP, user-agent, search terms. Signed-in consumers: email, name, picture (if you sign in with Google), and your saved favorites. Sales-alert subscribers (no sign-in required): email and an optional ZIP — never your name, phone, address, date of birth, MMJ card, or any health information. Dispensary owners: email, business name, addresses, phone, and the menu data you upload."],
      ["Sales-alert subscriptions",
        "If you opt in to sales alerts from the home page or footer, we store only your email and (optionally) your ZIP. We log the IP address you submitted from solely as proof of consent under the federal CAN-SPAM Act, and never expose that IP in any export or share it with any third party. Sales-alert emails will include the sender's physical mailing address and a one-click unsubscribe link, as required by CAN-SPAM. We will never tie your email to specific products you viewed, dispensaries you favorited, or purchases (we don't see purchases — Bud Link is not a dispensary). Unsubscribe at any time by clicking the link in any email or by emailing BudLink@LinkLitePro.com — we honor requests within 10 business days."],
      ["What we deliberately don't collect",
        "Bud Link is a search directory, not a dispensary. We do not collect Pennsylvania medical-marijuana card numbers, prescriptions, diagnoses, purchase history, consumption data, dates of birth (beyond the 21+ age gate), real-world addresses, or phone numbers from consumers. If a feature would require any of those, it is not built."],
      ["Why we collect what we do",
        "To run the search engine, personalize results by location and saved favorites, prevent abuse, send sales alerts you opted in to, and bill paid placements. We do NOT sell your data."],
      ["Location",
        "Location is shared only when you tap \"Use my current location\" or set a ZIP. We never read your GPS in the background."],
      ["Children",
        "Bud Link is strictly for adults 21+. We do not knowingly collect data from anyone under 21. If we discover such data we delete it."],
      ["Third parties we share with",
        "Stripe (payments — dispensary subscriptions only), Mapbox (map tiles), PostHog (anonymous product analytics), Google (only if you choose \"Sign in with Google\"). Sales-alert emails are stored in our database; if and when we begin sending them, the email service we use (e.g. Resend, Mailchimp) will be added here. No other third-party sharing. We never sell or rent your email."],
      ["Your rights",
        "Email BudLink@LinkLitePro.com to access, correct, or delete your data. We respond within 30 days. California, Virginia, Colorado, and Connecticut residents have additional rights under their state privacy laws."],
      ["Cookies",
        "We use one session cookie for sign-in (httpOnly, 90-day expiry) plus PostHog product-analytics cookies. No third-party ad cookies."],
      ["Security",
        "Passwords are bcrypt-hashed. API tokens are short-lived JWTs. Payment data lives at Stripe — we never see your card number."],
      ["Changes",
        "We will email signed-in users and active sales-alert subscribers at least 14 days before any material change to this policy."],
    ],
  },
  age: {
    title: "21+ Disclosure",
    eyebrow: "Compliance",
    body: [
      ["For adults only",
        "Bud Link displays information about legally-licensed Pennsylvania medical cannabis. Per Pennsylvania law (28 Pa. Code § 1141a.31), only certified patients age 21+ may purchase, possess, and consume medical cannabis in PA. (Minors with caregiver approval are excluded from Bud Link's audience.)"],
      ["Do not operate a vehicle",
        "Cannabis can impair coordination and judgment. Don't drive, bike, swim, or operate machinery while using it. PA DUI laws apply with zero tolerance to medical cardholders."],
      ["Pregnant or nursing",
        "The PA Department of Health does not recommend cannabis use during pregnancy or while breastfeeding."],
      ["Keep away from minors and pets",
        "Edibles can look like candy. Store sealed, labelled, and out of reach."],
      ["Talk to your provider",
        "Cannabis may interact with prescription medications, alcohol, and other substances. Always consult a Pennsylvania-certified medical-marijuana practitioner before changing your treatment."],
      ["No medical claims",
        "Bud Link does not endorse cannabis as a treatment for any condition. Information on this site is for product discovery, not diagnosis."],
      ["Help if you need it",
        "If you or someone you know is struggling with substance use, call SAMHSA's free, confidential helpline 24/7: 1-800-662-4357 (HELP)."],
    ],
  },
};

const Legal = ({ slug }) => {
  const s = SECTIONS[slug];
  if (!s) return null;
  return (
    <main className="container-narrow py-12 md:py-16 max-w-3xl">
      <div className="eyebrow mb-3">{s.eyebrow}</div>
      <h1 className="font-serif text-4xl md:text-5xl mb-2">{s.title}</h1>
      <p className="text-xs text-muted-foreground mb-10">
        Last updated · {today}
      </p>
      <div className="space-y-8 text-foreground/80 leading-relaxed">
        {s.body.map(([h, p]) => (
          <section key={h}>
            <h2 className="font-serif text-2xl text-foreground mb-2">{h}</h2>
            <p className="text-sm">{p}</p>
          </section>
        ))}
      </div>
      <div className="mt-12 pt-6 border-t border-border text-xs text-muted-foreground flex flex-wrap gap-5">
        <Link to="/terms" className="hover:text-foreground">Terms</Link>
        <Link to="/privacy" className="hover:text-foreground">Privacy</Link>
        <Link to="/21-plus" className="hover:text-foreground">21+ disclosure</Link>
        <Link to="/" className="hover:text-foreground">Back home</Link>
      </div>
    </main>
  );
};

export default Legal;
